import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Chip,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { IUserSearch } from '../shared-interfaces/IUserSearch';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../shared-interfaces/User/ICoreUser';
import UserService, { SearchTypes } from '../shared-services/User/UserService';

const UserSearch = (props: IUserSearch) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isControlled, _] = useState<boolean>(props.value !== undefined);
  const [values, setValues] = useState<ICoreUser | ICoreUser[]>(
    props.multiple ? ([] as ICoreUser[]) : ({} as ICoreUser),
  );
  const [unknownValues, setUnknownValues] = useState<string[]>([]);
  const [options, setOptions] = useState<ICoreUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // For Searching
  useEffect(() => {
    if (searchTerm.length < 3) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);

    const timeout = setTimeout(() => {
      (async () => {
        if (active) {
          let users = [] as ICoreUser[];
          let terms = searchTerm.split(/,|\n/).filter(x => x.trim() !== '');

          if (terms.length === 1) {
            if (props.assignableTo) {
              users = await UserService.searchAssignableUser(
                props.assignableTo,
                terms[0],
                props.roleRestriction,
                25,
                0,
              );
            } else if (props.inNetwork) {
              await UserService.search({
                term: terms[0],
                inNetwork: true,
                RoleIds: props.roleRestriction,
                skip: 0,
                take: 25,
                enrolledOnly: false,
                SearchType: SearchTypes.like,
              }).then(u => {
                users = u.items;
              });
            } else {
              users = await UserService.searchCoreUser({
                term: terms[0],
                roles: props.roleRestriction,
                retentionStatuses: [],
                take: 25,
                skip: 0,
              });
            }
          } else if (props.multiple) {
            if (props.maxSearchLength && terms.length > props.maxSearchLength) {
              if (props.onSearchLengthError) props.onSearchLengthError(true);
              terms.length = props.maxSearchLength;
            }

            const filteredTerms = Array.from(
              new Set(
                terms
                  .filter(
                    x =>
                      (
                        (isControlled ? props.value : values) as ICoreUser[]
                      ).findIndex(y => x === y.externalId) === -1,
                  )
                  .filter(
                    x => (unknownValues ?? []).findIndex(y => x === y) === -1,
                  ),
              ),
            );
            const result = await UserService.multiSearchCoreUser(
              filteredTerms,
              props.roleRestriction,
            );
            const newValue = [
              //@ts-ignore
              ...(isControlled ? props.value : values),
              ...result.filter(
                x =>
                  (
                    (isControlled ? props.value : values) as ICoreUser[]
                  ).findIndex(y => y.id === x.id) === -1,
              ),
            ];

            const newUnknownValues = Array.from(
              new Set([
                ...(unknownValues ?? []),
                ...filteredTerms
                  .filter(
                    x =>
                      result.findIndex(
                        y =>
                          y.externalId.toLocaleUpperCase().trim() ===
                          x.toLocaleUpperCase().trim(),
                      ) === -1,
                  )
                  .filter(
                    x => (unknownValues ?? []).findIndex(y => y === x) === -1,
                  ),
              ]),
            );

            props.onSelect(newValue);
            setValues(newValue);
            setUnknownValues(newUnknownValues);
            setSearchTerm('');
            setOpen(false);
          }
          if (active) {
            setOptions(users);
            setLoading(false);
          }
        }
      })();
    }, 250);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [searchTerm]);

  // Open/Close
  useEffect(() => {
    if (!open) setOptions([]);
  }, [open]);

  // Unknown chips
  const unknownChips = () => {
    return (unknownValues ?? []).map((x, i) => (
      <Chip
        key={`unknown${i}`}
        label={x}
        color="secondary"
        style={{ marginLeft: '5px' }}
        onClick={() => {
          setSearchTerm(x);
          setUnknownValues(unknownValues.filter(y => y !== x));
          setOpen(true);
        }}
        onDelete={() => {
          setUnknownValues(unknownValues.filter(y => y !== x));
        }}
      />
    ));
  };

  const disableChip = (user: ICoreUser) => {
    if (!props.disabledValues || !props.multiple) return false;

    if (props.disabledValues.findIndex(x => x.id === user.id) > -1) {
      return true;
    } else {
      return false;
    }
  };

  const checkForInput = () => {
    if (props.multiple) {
      if (props.disabledValues && props.disabledValues.length > 0) {
        return true;
      }

      if (searchTerm === '' && (values as ICoreUser[]).length === 0) {
        return false;
      } else if (searchTerm !== '' && (values as ICoreUser[]).length === 0) {
        return true;
      } else if (searchTerm === '' && (values as ICoreUser[]).length > 0) {
        return true;
      } else {
        return true;
      }
    } else if (!props.multiple) {
      if (searchTerm === '' && !(values as ICoreUser).id) {
        return false;
      } else if (searchTerm !== '' && !(values as ICoreUser).id) {
        return true;
      } else if (searchTerm === '' && (values as ICoreUser).id) {
        return true;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  return (
    <Autocomplete
      inputValue={searchTerm !== '' ? searchTerm : undefined}
      onInputChange={(e, v, r) => setSearchTerm(v)}
      value={isControlled ? props.value : values}
      open={open}
      multiple={props.multiple}
      disabled={props.disabled}
      options={options}
      loading={loading}
      disableClearable={!props.clearable}
      noOptionsText={
        loading
          ? 'Loading...'
          : !loading && searchTerm.length >= 3 && options.length === 0
          ? 'No Results Found'
          : 'Type to Search'
      }
      onOpen={() => setOpen(true)}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option: ICoreUser, value: ICoreUser) => {
        return option.id === value.id;
      }}
      getOptionLabel={option => GetCoreUserFormattedName(option)}
      onChange={(e, v, t) => {
        setValues(v ? v : ({} as ICoreUser));
        props.onSelect(v ? v : ({} as ICoreUser));
        setSearchTerm('');
      }}
      renderInput={params => {
        params.InputLabelProps = {
          //@ts-ignore
          shrink: checkForInput(),
          ...params.InputLabelProps,
        };
        params.inputProps = {
          'aria-label': props.label,
          ...params.inputProps,
        };
        return (
          <>
            <TextField
              label={props.label}
              {...params}
              aria-label={props.label}
              multiline
              variant={props.variant ? props.variant : 'outlined'}
              size={props.size ? props.size : 'medium'}
              value={searchTerm !== '' ? searchTerm : undefined}
              onBlur={props.onBlur ? props.onBlur : undefined}
              error={props.error}
              style={{ backgroundColor: '#FFF' }}
              inputProps={{
                ...params.inputProps,
                style: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              InputProps={{
                ...params.InputProps,
                placeholder: props.placeHolder,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                startAdornment: (
                  <>
                    {props.placeHolder && (
                      <SearchIcon style={{ color: '#a1a1a1' }} />
                    )}
                    {params.InputProps.startAdornment ?? <>{unknownChips()}</>}
                  </>
                ),
                style: {
                  marginTop: 0,
                },
              }}
            />
          </>
        );
      }}
      renderTags={(value, getTagProps) => {
        const values = value.map((x, i) => {
          const tagProps = getTagProps({ index: i });
          const disable = disableChip(x);
          //@ts-ignore
          tagProps.disabled = disable;
          //@ts-ignore
          if (disable) tagProps.onDelete = undefined;
          return (
            <Tooltip
              title={
                disable
                  ? 'Additional Audience cannot be removed once added'
                  : ''
              }
              key={i}
            >
              <span>
                <Chip
                  avatar={
                    <Avatar
                      alt={GetCoreUserFormattedName(x)}
                      src={
                        x.profileImageFilePath ??
                        x.institutionProfileImageFilePath
                      }
                    />
                  }
                  label={GetCoreUserFormattedName(x)}
                  style={{ opacity: '1' }}
                  clickable={false}
                  {...tagProps}
                />
              </span>
            </Tooltip>
          );
        });
        return [...values, ...unknownChips()];
      }}
      renderOption={(p, o) => {
        return (
          <ListItem {...p}>
            <ListItemAvatar>
              <Avatar
                alt={GetCoreUserFormattedName(o, false)}
                src={
                  o.profileImageFilePath ?? o.institutionProfileImageFilePath
                }
              />
            </ListItemAvatar>
            <ListItemText
              primary={GetCoreUserFormattedName(o, false)}
              secondary={o.externalId}
            />
          </ListItem>
        );
      }}
    />
  );
};

export default UserSearch;
